import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { LookupListOption } from '../../core/models/lookup-list.model';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { LookupListService } from '../../core/services/lookup-list.service';
import { TranslationService } from '../../core/services/translation.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { getLookupListEditFields } from './lookup-list-edit.definition';

interface LookupListFormModel {
  name: string;
  is_active: boolean;
  /** Keeps the options-editor's "Promote to Lookup List" button hidden. */
  optionsSource: 'lookup-list';
  options: LookupListOption[];
}

@Component({
  selector: 'app-lookup-list-edit',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    IconComponent,
    ButtonComponent,
    SpinnerComponent,
    TranslatePipe,
  ],
  templateUrl: './lookup-list-edit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './lookup-list-edit.component.css',
})
export class LookupListEditComponent implements OnInit {
  private lookupListService = inject(LookupListService);
  private translate = inject(TranslationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private breadcrumbService = inject(BreadcrumbService);

  // Page state
  loading = signal(true);
  saving = signal(false);
  error = signal<string | null>(null);
  isEditMode = signal(false);
  formValid = signal(false); // Track form validity to avoid ExpressionChangedAfterItHasBeenCheckedError

  // Computed disabled state for save button
  saveDisabled = computed(() => !this.formValid() || this.saving());

  // ── Single Formly form ────────────────────────────────────────────────────
  form = new FormGroup({});
  model: LookupListFormModel = {
    name: '',
    is_active: true,
    optionsSource: 'lookup-list',
    options: [],
  };
  fields: FormlyFieldConfig[] = [];

  private listId: string | null = null;

  async ngOnInit(): Promise<void> {
    // Defer initialization to avoid ExpressionChangedAfterItHasBeenCheckedError
    await Promise.resolve();

    this.fields = getLookupListEditFields((key) => this.translate.translate(key));

    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'new') {
      this.listId = id;
      this.isEditMode.set(true);
      await this.loadList(id);
    } else {
      // New lookup list — set breadcrumb
      this.breadcrumbService.setDynamicLabel('New Lookup List');
      // New list — start with two default options.
      // Values use hyphens to match generateKeyFromLabel output so the
      // auto-fill guard in OptionsEditorFormlyTypeComponent keeps updating
      // the value as the user types the label.
      this.model = {
        name: '',
        is_active: true,
        optionsSource: 'lookup-list',
        options: [
          { label: 'Option 1', value: 'option-1' },
          { label: 'Option 2', value: 'option-2' },
        ],
      };
      this.loading.set(false);
    }

    // Watch for form validity changes
    this.form.statusChanges.subscribe(() => {
      this.formValid.set(this.form.valid);
    });
    this.form.valueChanges.subscribe(() => {
      this.formValid.set(this.form.valid);
    });

    // Set initial form validity
    this.formValid.set(this.form.valid);
  }

  private async loadList(id: string): Promise<void> {
    try {
      const list = await this.lookupListService.getById(id);
      if (!list) {
        this.error.set('Lookup list not found.');
        return;
      }
      this.breadcrumbService.setDynamicLabel(list.name);
      this.model = {
        name: list.name,
        is_active: list.is_active === 1,
        optionsSource: 'lookup-list',
        options: list.options.map((o) => ({ ...o })),
      };
    } catch (err) {
      this.error.set('Failed to load lookup list.');
    } finally {
      this.loading.set(false);
    }
  }

  onCancel(): void {
    this.router.navigate(['/lookup-lists']);
  }

  async save(): Promise<void> {
    if (this.saveDisabled()) return;

    this.saving.set(true);
    this.error.set(null);

    const { name, is_active, options } = this.model;

    try {
      if (this.isEditMode() && this.listId) {
        await this.lookupListService.update({
          id: this.listId,
          name: name.trim(),
          options: options ?? [],
          is_active: is_active ? 1 : 0,
        });
      } else {
        await this.lookupListService.create({
          name: name.trim(),
          options: options ?? [],
        });
      }
      this.router.navigate(['/lookup-lists']);
    } catch (err: any) {
      this.error.set(err?.message ?? 'Failed to save lookup list.');
    } finally {
      this.saving.set(false);
    }
  }
}
